import { useState } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { formatMoney, formatPercent } from '@lib/utils';

// ---------------------------------------------------------

import Button from '@components/button';
import Link from '@components/link';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  container,
  container_left,
  container_right,
  recalculate,
  disclaimer,
  header,
  form_wrapper,
  total_price,
  total_label,
  detail_container,
  error,
  form_left,
  form_right
} = styles;

import inputStyles from '../inputs/styles.module.scss';
import selectStyles from '../select/styles.module.scss';
const { input_container, input_field, input_label } = inputStyles;
const { select_container, select } = selectStyles;

// ---------------------------------------------------------

const MortgageCalculator = ({ price, currentRates, lastUpdatedOn }) => {
  const [calcIntRate, setCalcIntRate] = useState(currentRates?.[30] || 5);
  const [calcTerm, setCalcTerm] = useState(30);

  const initialValues = {
    calcLoan: price ? price * 0.8 : 900000,
    calcPrice: price ? price : 1000000
  };

  const z = 1 + calcIntRate / 1200;
  const m = calcTerm * 12;
  const r = Math.pow(z, m);
  const initialTotal = (initialValues.calcLoan * ((calcIntRate / 1200) * r)) / (r - 1);

  // ---------------------------------------------------------

  const [calcValues, setCalcValues] = useState(initialValues);
  const [calcTotal, setCalcTotal] = useState(initialTotal);
  const [exceedsError, setExceedsError] = useState(false);

  const handleCalcChange = (event) => {
    const { name, value } = event.target;

    setCalcValues((calcValues) => {
      const newCalcValues = {
        ...calcValues,
        [name]: parseFloat(value.replace(/\$|,/g, '')) || setCalcValues({ ...initialValues })
      };
      return newCalcValues;
    });
  };

  const handleRecalculate = (event) => {
    if (event.preventDefault) event.preventDefault();
    const name = event?.target?.name;
    const value = event?.target?.value;

    const updatedCalcIntRate = event.name === 'calcIntRate' ? event.value : calcIntRate;

    if (calcValues.calcLoan / calcValues.calcPrice > 0.95) {
      setExceedsError(true);
      setCalcTotal(0);
    } else {
      setExceedsError(false);
      setCalcValues((calcValues) => {
        const newCalcValues = {
          ...calcValues
        };
        if (event) newCalcValues[name] = parseFloat(value);
        return newCalcValues;
      });

      const z = 1 + updatedCalcIntRate / 1200;
      const m = calcTerm * 12;
      const r = Math.pow(z, m);
      const newTotal = (calcValues.calcLoan * ((updatedCalcIntRate / 1200) * r)) / (r - 1);

      setCalcTotal(newTotal);
    }
  };

  // ---------------------------------------------------------

  const { calcPrice, calcLoan } = calcValues;

  return (
    <div className={container}>
      <div className={container_left}>
        <ReactSVG src="/images/logos/icons-logos-sibcy-horizontal-mortgage.svg" />
        <h3 className={header}>Mortgage Calculator</h3>
        <form onSubmit={handleRecalculate} method="post">
          <div className={form_wrapper}>
            <div className={form_left}>
              <div className={input_container}>
                <label className={input_label}>
                  <span>Purchase Price</span>
                  <input
                    className={input_field}
                    placeholder={formatMoney(calcPrice)}
                    onChange={handleCalcChange}
                    type="text"
                    name="calcPrice"
                  />
                </label>
              </div>
              <div className={input_container}>
                <label className={input_label}>
                  <span>Loan Amount</span>
                  <input
                    className={input_field}
                    placeholder={formatMoney(calcLoan)}
                    onChange={handleCalcChange}
                    type="text"
                    name="calcLoan"
                  />
                </label>
              </div>
            </div>
            <div className={form_right}>
              <div className={input_container}>
                <label className={input_label}>
                  <span>Interest Rate (%)</span>
                  <input
                    className={input_field}
                    placeholder={formatPercent(calcIntRate)}
                    onChange={(e) => setCalcIntRate(e.target.value)}
                    value={calcIntRate}
                    type="text"
                    name="calcIntRate"
                  />
                </label>
              </div>
              <div className={select_container}>
                <label htmlFor="termyears">Term Years</label>
                <div className={select}>
                  <select
                    id="termyears"
                    onChange={(e) => {
                      setCalcIntRate(currentRates[e.target.value]);
                      setCalcTerm(e.target.value);
                    }}
                    name="calcTerm"
                  >
                    <option value="30">30 yr Fix</option>
                    <option value="15">15 yr Fix</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <Button
            className={recalculate}
            label="Recalculate"
            onClick={() => {}}
            theme="text-only"
            type="submit"
          />
        </form>
        <div className={disclaimer}>
          *The rates & payments shown are illustrative only.
          <br />
          Payment displayed does not include taxes and insurance. Rates last updated on{' '}
          {new Date(lastUpdatedOn).toLocaleDateString()} from Freddie Mac Primary Mortgage Market
          Survey.{' '}
          <Link url="https://mortgage.sibcycline.com/findloanofficer">Contact a loan officer</Link>{' '}
          for actual rate/payment quotes.
        </div>
      </div>
      <div className={container_right}>
        <div className={total_price}>{formatMoney(calcTotal)}*</div>
        <div className={total_label}>Estimated monthly total</div>
        <div className={detail_container}>
          <span className={error}>
            {exceedsError && 'Error! Loan amount exceeds 95% of price.'}
          </span>
        </div>
        <Button
          type="button"
          label="Get Pre-Approved"
          url="https://mortgage.sibcycline.com/jumpstart"
        />
      </div>
    </div>
  );
};

MortgageCalculator.propTypes = {
  /**
   * Current interest rates
   */
  currentRates: PropTypes.shape({
    15: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    30: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /**
   * Specifies the price of a property (number)
   */
  price: PropTypes.number,
  /**
   * Specifies the property URL
   */
  url: PropTypes.string,
  /**
   * Specifies the date the rates were last updated
   */
  lastUpdatedOn: PropTypes.string
};

export default MortgageCalculator;
